import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_RUNTIME_SHELL_LANGUAGE,
  RUNTIME_SHELL_LANGUAGES,
  getRuntimeShellDirection,
  normalizeRuntimeShellLanguage,
  type RuntimeShellDirection,
  type RuntimeShellLanguage
} from "@/i18n/localeRegistry";
import enCommon from "@/i18n/locales/en/common.json" with { type: "json" };
import {
  PSEUDO_LOCALE_QA_LANGUAGE,
  PSEUDO_LOCALE_STORAGE_KEY,
  createPseudoLocaleCatalog
} from "@/i18n/pseudoLocale";

export {
  DEFAULT_RUNTIME_SHELL_LANGUAGE,
  FIRST_PRODUCTION_LANGUAGE_TARGETS,
  LOCALE_REGISTRY,
  RUNTIME_SHELL_DIRECTIONS,
  RUNTIME_SHELL_LANGUAGES,
  SOURCE_LOCALE,
  normalizeLocaleTag,
  normalizeRuntimeShellLanguage,
  type LocaleActivationStatus,
  type LocaleRegistryEntry,
  type LocaleTag,
  type RuntimeShellDirection,
  type RuntimeShellLanguage
} from "@/i18n/localeRegistry";
export { PSEUDO_LOCALE_QA_LANGUAGE, PSEUDO_LOCALE_STORAGE_KEY } from "@/i18n/pseudoLocale";

export const LANGUAGE_STORAGE_KEY = "6529-safe-app.language";
export const DIRECTION_STORAGE_KEY = "6529-safe-app.direction";
export const DEFAULT_RUNTIME_SHELL_DIRECTION: RuntimeShellDirection = "ltr";

let languageSynchronizationAttached = false;

function getActiveRuntimeShellLanguage(): RuntimeShellLanguage {
  return normalizeRuntimeShellLanguage(i18n.resolvedLanguage ?? i18n.language);
}

function normalizeRuntimeShellDirection(
  value: string | null | undefined
): RuntimeShellDirection | null {
  if (typeof value !== "string") return null;

  const canonical = value.trim().toLowerCase();
  if (canonical === "ltr" || canonical === "rtl") {
    return canonical as RuntimeShellDirection;
  }

  return null;
}

function resolveRuntimeShellDirection(language: RuntimeShellLanguage): RuntimeShellDirection {
  return getRuntimeShellDirection(language) ?? DEFAULT_RUNTIME_SHELL_DIRECTION;
}

function readStoredDirectionOverride(): RuntimeShellDirection | null {
  if (typeof window === "undefined") return null;

  try {
    return normalizeRuntimeShellDirection(window.localStorage.getItem(DIRECTION_STORAGE_KEY));
  } catch {
    return null;
  }
}

function getEffectiveRuntimeShellDirection(language: RuntimeShellLanguage): RuntimeShellDirection {
  return readStoredDirectionOverride() ?? resolveRuntimeShellDirection(language);
}

function readStoredLanguagePreference(): RuntimeShellLanguage {
  if (typeof window === "undefined") return DEFAULT_RUNTIME_SHELL_LANGUAGE;

  try {
    return normalizeRuntimeShellLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return DEFAULT_RUNTIME_SHELL_LANGUAGE;
  }
}

function isPseudoLocaleQaEnvironment(): boolean {
  return import.meta.env.DEV || import.meta.env.MODE === "test";
}

function isPseudoLocaleQaLanguage(value: string | null | undefined): boolean {
  return value === PSEUDO_LOCALE_QA_LANGUAGE;
}

function readPseudoLocaleQaPreference(): string | null {
  if (typeof window === "undefined" || !isPseudoLocaleQaEnvironment()) return null;

  try {
    const value = window.localStorage.getItem(PSEUDO_LOCALE_STORAGE_KEY);
    return isPseudoLocaleQaLanguage(value) ? PSEUDO_LOCALE_QA_LANGUAGE : null;
  } catch {
    return null;
  }
}

function applyPseudoLocaleQaAttribute(language: string): void {
  if (typeof document === "undefined") return;
  if (isPseudoLocaleQaEnvironment() && isPseudoLocaleQaLanguage(language)) {
    document.documentElement.dataset.pseudoLocale = PSEUDO_LOCALE_QA_LANGUAGE;
    return;
  }
  delete document.documentElement.dataset.pseudoLocale;
}

function persistLanguagePreference(language: RuntimeShellLanguage): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore storage failures (quota/privacy mode), app remains deterministic via i18n state.
  }
}

function applyDocumentLanguage(language: RuntimeShellLanguage): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = language;
}

function applyDocumentDirection(direction: RuntimeShellDirection): void {
  if (typeof document === "undefined") return;
  document.documentElement.dir = direction;
}

function syncLanguageSideEffects(language: string): void {
  const normalizedLanguage = normalizeRuntimeShellLanguage(language);
  const effectiveDirection = getEffectiveRuntimeShellDirection(normalizedLanguage);
  persistLanguagePreference(normalizedLanguage);
  applyDocumentLanguage(normalizedLanguage);
  applyDocumentDirection(effectiveDirection);
  applyPseudoLocaleQaAttribute(language);
}

function ensureLanguageSynchronization(): void {
  if (languageSynchronizationAttached) return;
  i18n.on("languageChanged", syncLanguageSideEffects);
  languageSynchronizationAttached = true;
}

export function getRuntimeShellLanguagePreference(): RuntimeShellLanguage {
  if (i18n.isInitialized) {
    return getActiveRuntimeShellLanguage();
  }
  return readStoredLanguagePreference();
}

export async function setRuntimeShellLanguagePreference(
  language: string
): Promise<RuntimeShellLanguage> {
  const normalizedLanguage = normalizeRuntimeShellLanguage(language);
  if (!i18n.isInitialized) {
    syncLanguageSideEffects(normalizedLanguage);
    return normalizedLanguage;
  }

  const activeLanguage = getActiveRuntimeShellLanguage();
  if (activeLanguage === normalizedLanguage) {
    syncLanguageSideEffects(activeLanguage);
    return activeLanguage;
  }

  try {
    await i18n.changeLanguage(normalizedLanguage);
  } catch (error) {
    // Keep storage/document metadata aligned with the language i18n actually retained.
    const synchronizedLanguage = getActiveRuntimeShellLanguage();
    syncLanguageSideEffects(synchronizedLanguage);
    throw error;
  }

  const synchronizedLanguage = getActiveRuntimeShellLanguage();
  syncLanguageSideEffects(synchronizedLanguage);
  return synchronizedLanguage;
}

export async function initI18n(): Promise<void> {
  const preferredLanguage = readStoredLanguagePreference();
  const pseudoLocaleQaLanguage = readPseudoLocaleQaPreference();
  const initialI18nLanguage = pseudoLocaleQaLanguage ?? preferredLanguage;
  syncLanguageSideEffects(initialI18nLanguage);

  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      lng: initialI18nLanguage,
      fallbackLng: DEFAULT_RUNTIME_SHELL_LANGUAGE,
      supportedLngs: [
        ...RUNTIME_SHELL_LANGUAGES,
        ...(pseudoLocaleQaLanguage ? [PSEUDO_LOCALE_QA_LANGUAGE] : [])
      ],
      interpolation: { escapeValue: false },
      resources: {
        en: { common: enCommon },
        "en-US": { common: enCommon },
        "en-GB": { common: enCommon },
        ...(pseudoLocaleQaLanguage
          ? { [PSEUDO_LOCALE_QA_LANGUAGE]: { common: createPseudoLocaleCatalog(enCommon) } }
          : {})
      },
      defaultNS: "common"
    });
  } else if (i18n.language !== initialI18nLanguage) {
    if (pseudoLocaleQaLanguage && !i18n.hasResourceBundle(PSEUDO_LOCALE_QA_LANGUAGE, "common")) {
      i18n.addResourceBundle(
        PSEUDO_LOCALE_QA_LANGUAGE,
        "common",
        createPseudoLocaleCatalog(enCommon),
        true,
        true
      );
    }
    await i18n.changeLanguage(initialI18nLanguage);
  } else if (!pseudoLocaleQaLanguage && getActiveRuntimeShellLanguage() !== preferredLanguage) {
    await i18n.changeLanguage(preferredLanguage);
  }

  ensureLanguageSynchronization();
  syncLanguageSideEffects(i18n.resolvedLanguage ?? i18n.language);
}
